import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { API_URLS, API_CONFIG } from '../../config/api';
import { ExternalLink, Building2, Globe, ShoppingCart, Smartphone, Code, TrendingUp, Palette } from 'lucide-react';

function ReferenceCard({ item }) {
  const BASE_URL = API_CONFIG.BASE_URL;
  const normalizeUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) return `${BASE_URL}${url}`;
    return `${BASE_URL}/${url}`;
  };
  
  return (
    <Link 
      to={`/referanslar/${item.slug || item.id}`}
      className="group block overflow-hidden transition-all duration-300"
    >
      <div className="aspect-video overflow-hidden flex items-center justify-center">
        {item.logo ? (
          <img
            src={normalizeUrl(item.logo)}
            alt={item.name}
            className="w-full h-full object-contain transition-all duration-300 group-hover:scale-110 filter grayscale group-hover:grayscale-0"
            onError={(e) => { e.target.src = 'https://placehold.co/400x200/e0e7ff/6366f1?text=' + encodeURIComponent(item.name); }}
          />
        ) : (
          <div className="text-center">
            <Building2 className="w-16 h-16 text-gray-400 group-hover:text-blue-600 transition-colors mx-auto" />
          </div>
        )}
      </div>
    </Link>
  );
}

const categoryIcons = {
  'web-siteleri': Globe,
  'e-ticaret': ShoppingCart,
  'mobil-uygulama': Smartphone,
  'yazilim': Code,
  'dijital-pazarlama': TrendingUp,
  'tasarim': Palette,
};

export default function ReferencesPage() {
  const [references, setReferences] = useState([]);
  const [filter, setFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch references and categories
        const [refRes, catRes] = await Promise.all([
          fetch(API_URLS.REFERENCES),
          fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=getReferenceCategories`)
        ]);
        
        const refData = await refRes.json();
        const catData = await catRes.json();
        
        if (Array.isArray(refData)) {
          setReferences(refData);
        } else if (refData?.status === 'success' && Array.isArray(refData.data)) {
          setReferences(refData.data);
        }
        
        if (catData?.status === 'success' && Array.isArray(catData.data)) {
          const activeCats = catData.data.filter(c => c.is_active);
          setCategories(activeCats);
        }
      } catch (e) {
        console.error('Error fetching references:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filtered = filter === 'all' 
    ? references 
    : references.filter(r => r.category_id == filter);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-purple-50">
      {/* Hero */}
      <section 
        className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 py-20 overflow-hidden"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Referanslarımız
            </h1>
            <p className="mt-4 text-xl text-blue-100 max-w-3xl mx-auto">
              Başarı hikayelerimize göz atın. Farklı sektörlerden müşterilerimize sunduğumuz çözümler.
            </p>
            <div className="mt-8 flex items-center justify-center gap-4 text-blue-100">
              <div className="flex items-center">
                <Building2 className="w-5 h-5 mr-2" />
                <span>{references.length}+ Proje</span>
              </div>
              <div className="flex items-center">
                <Globe className="w-5 h-5 mr-2" />
                <span>{categories.length} Kategori</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section 
        className="py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Categories */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="sticky top-24">
                <Card className="border border-gray-200 shadow-md">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Kategoriler</h3>
                    <div className="space-y-2">
                      <button
                        onClick={() => setFilter('all')}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                          filter === 'all'
                            ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-200'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span className="font-medium">Tümü</span>
                        <Badge variant="secondary" className="bg-white">
                          {references.length}
                        </Badge>
                      </button>
                      
                      {categories.map((cat) => {
                        const Icon = categoryIcons[cat.slug] || Building2;
                        const count = references.filter(r => r.category_id == cat.id).length;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => setFilter(cat.id)}
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                              filter == cat.id
                                ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-200'
                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <Icon className="w-5 h-5" />
                              <span className="font-medium">{cat.name}</span>
                            </div>
                            <Badge variant="secondary" className="bg-white">
                              {count}
                            </Badge>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </aside>

            {/* Main Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-600">Referanslar yükleniyor...</p>
                  </div>
                </div>
              ) : filtered.length === 0 ? (
                <Card className="p-12 text-center">
                  <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Henüz referans bulunmuyor
                  </h3>
                  <p className="text-gray-500">
                    {filter === 'all' ? 'Sistemde referans kaydı yok.' : 'Bu kategoride referans bulunmuyor.'}
                  </p>
                </Card>
              ) : (
                <>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {filter === 'all' 
                        ? `Tüm Referanslar (${filtered.length})` 
                        : `${categories.find(c => c.id == filter)?.name || 'Kategori'} (${filtered.length})`
                      }
                    </h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filtered.map((item, idx) => (
                      <ReferenceCard key={item.id || idx} item={item} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

